import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export interface SearchableOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  allowCustom?: boolean;
  required?: boolean;
  className?: string;
  /** Optional id for the trigger button so an external <label htmlFor> resolves. */
  id?: string;
  /** When true the trigger is locked (used for edit-mode read-only fields). */
  disabled?: boolean;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  allowCustom = false,
  className,
  id,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = options.find((o) => o.value === value);
  const display = selected?.label ?? value ?? "";

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          className={cn(
            "w-full mt-1.5 flex items-center justify-between gap-2 px-3 py-2 bg-background border border-border/50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 hover:border-border transition cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed hover:border-border/50",
            !display && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate text-left">{display || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] rounded-xl" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search…"
          />
          {/* CSR-1177: when this Popover is opened inside a modal Dialog (e.g.
              Add Policy), Radix Dialog's react-remove-scroll locks wheel/touch
              scrolling on everything outside the dialog subtree. This list is
              portaled outside that subtree, so without stopping propagation the
              wheel event bubbles to document where react-remove-scroll
              preventDefaults it — the carrier list renders but won't scroll.
              The listeners are bubble-phase (react-remove-scroll@2 binds
              non-capture), so stopping propagation here lets the list scroll. */}
          <CommandList
            className="max-h-64"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty>
              {allowCustom && search.trim() ? (
                <button
                  type="button"
                  onClick={() => {
                    onChange(search.trim());
                    setOpen(false);
                    setSearch("");
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted rounded-md cursor-pointer"
                >
                  Use "<span className="font-medium">{search.trim()}</span>"
                </button>
              ) : (
                <span className="block px-3 py-4 text-sm text-muted-foreground text-center">
                  No results.
                </span>
              )}
            </CommandEmpty>
            <CommandGroup>
              {options
                .filter((o) =>
                  o.label.toLowerCase().includes(search.toLowerCase()),
                )
                .map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => {
                      onChange(o.value);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Check
                      className={cn(
                        "w-4 h-4 mr-2",
                        value === o.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {o.label}
                  </CommandItem>
                ))}
              {allowCustom &&
                search.trim() &&
                !options.some(
                  (o) => o.label.toLowerCase() === search.trim().toLowerCase(),
                ) && (
                  <CommandItem
                    value={`__custom_${search}`}
                    onSelect={() => {
                      onChange(search.trim());
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Check className="w-4 h-4 mr-2 opacity-0" />
                    <span>
                      Use "<span className="font-medium">{search.trim()}</span>"
                    </span>
                  </CommandItem>
                )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
